import { forwardRef, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  hint?: string;
  error?: string;
  /** Visually hide the label but keep it for screen readers. */
  hideLabel?: boolean;
  iconLeft?: ReactNode;
  suffix?: ReactNode;
}

const fieldBase =
  "w-full rounded-md bg-surface text-body placeholder:text-body-faint " +
  "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast " +
  "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none " +
  "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60";

/**
 * Labelled text input with hint + error states. The label is always present
 * (visually hideable for compact rows) and wired to the input via htmlFor /
 * aria-describedby so screen readers announce help and validation text.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    { label, hint, error, hideLabel, iconLeft, suffix, id, className, required, ...rest },
    ref,
  ) => {
    const auto = useId();
    const fieldId = id ?? auto;
    const hintId = hint ? `${fieldId}-hint` : undefined;
    const errId = error ? `${fieldId}-err` : undefined;

    return (
      <div className="w-full">
        <label
          htmlFor={fieldId}
          className={cn(
            "mb-1.5 block font-display text-sm font-bold text-body",
            hideLabel && "sr-only",
          )}
        >
          {label}
          {required && <span className="ml-1 text-ember">*</span>}
        </label>

        <div className="relative">
          {iconLeft && (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-body-faint">
              {iconLeft}
            </span>
          )}
          <input
            ref={ref}
            id={fieldId}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={cn(hintId, errId) || undefined}
            className={cn(
              fieldBase,
              "h-12 text-base",
              iconLeft ? "pl-10" : "pl-4",
              suffix ? "pr-12" : "pr-4",
              error && "ring-2 ring-danger focus:ring-danger",
              className,
            )}
            {...rest}
          />
          {suffix && (
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-body-muted">
              {suffix}
            </span>
          )}
        </div>

        {error ? (
          <p id={errId} className="mt-1.5 text-sm font-medium text-danger">
            {error}
          </p>
        ) : (
          hint && (
            <p id={hintId} className="mt-1.5 text-sm text-body-muted">
              {hint}
            </p>
          )
        )}
      </div>
    );
  },
);
TextField.displayName = "TextField";
